import { QueryFunction } from '@tanstack/react-query';
import {
  Coordinates,
  ExtractedFieldResponse,
  ExtractedFieldType,
  Table,
} from './types';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface DocField {
  id: string;
  displayText: string;
  confidence: number;
  threshold?: number | null;
  value: string | null;
  coordinates: Coordinates;
  options: {
    id: string;
    value: string;
  }[];
  pageId?: string;
  field_type_id?: string;
  fieldTypeId?: string;
  fieldType?: ExtractedFieldType | null;
  tableValue: Table | null;
  userApproved?: boolean;
  encompassFields?: {
    fieldId: string;
    fieldName?: string;
    value: string;
    loanBorrowerId?: string | null;
  }[];
}

export interface GetDocumentFields {
  fields: DocField[];
  status: string;
}

interface GetDocumentFieldResponse {
  status: string;
  extractedData: ExtractedFieldResponse[];
  nextCursor: string | null;
}

const getDocumentFields: QueryFunction<
  GetDocumentFields,
  ['/document-fields', string]
> = async ({ queryKey }) => {
  const source = queryKey[1];
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetDocumentFieldResponse>(
    `/extractedData/documents/${source}`,
    {
      method: 'GET',
      paginate: true,
      accumulateKey: 'extractedData',
      nextCursorKey: 'nextCursor',
      limit: 100,
    }, // Type assertion to allow custom props
  );
  const status: string = response.data?.status.toString() ?? '';
  const allFields: DocField[] = [];
  response.data?.extractedData.forEach((field: ExtractedFieldResponse) => {
    allFields.push({
      id: field.id ?? '',
      displayText: '',
      confidence: field.fieldConfidence ?? 0,
      threshold: field.effectiveThreshold ?? null,
      value: field.fieldValue ?? null,
      coordinates:
        field.boundingBoxes ??
        ({ top: 0, left: 0, bottom: 0, right: 0 } as Coordinates),
      options: [],
      pageId: field.pageId ?? '',
      field_type_id: field.field_type_id ?? '',
      fieldTypeId: field.fieldTypeId ?? '',
      fieldType: field.fieldType ?? null,
      tableValue: field.tableValue,
      userApproved: field.userApproved ?? false,
      encompassFields: field.encompassFields ?? [],
    });
  });

  return { fields: allFields, status: status == 'true' ? 'success' : 'error' };
};

export default getDocumentFields;
